import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, CheckCircle2, XCircle, Loader2, LogOut } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/contexts/AuthContext";

const CLASS_LEVELS = ["Class 9", "Class 10", "Class 11", "Class 12", "Undergraduate", "Postgraduate"];

const INTEREST_TAGS = [
  "Coding & Technology", "Science & Research", "Art & Design", "Music & Performance",
  "Writing & Storytelling", "Sports & Fitness", "Helping Others / Volunteering",
  "Business & Entrepreneurship", "Leadership & Public Speaking", "Organizing & Planning Events",
  "Nature & Outdoors", "Gaming & Strategy", "Cooking", "Building / DIY / Mechanical",
  "Debating & Persuasion", "Numbers, Finance & Investing",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [step, setStep] = useState("profile");
  const [form, setForm] = useState({
    first_name: "", last_name: "", dob: "", phone: "",
    country: "", state: "", class_level: "", school: "",
  });
  const [interestTags, setInterestTags] = useState([]);
  const [interestNotes, setInterestNotes] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [attribution, setAttribution] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralStatus, setReferralStatus] = useState("idle");
  const [referralExecName, setReferralExecName] = useState("");
  const [referralLocked, setReferralLocked] = useState(false);

  useEffect(() => {
    api.get("/student/profile").then((res) => {
      if (res.data?.profile_completed) setStep("phone");
    }).catch(() => {});

    const raw = localStorage.getItem("csr_attribution");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.referral_code) {
          setAttribution(parsed);
          setReferralCode(parsed.referral_code);
          setReferralLocked(true);
          validateReferralCode(parsed.referral_code);
        }
      } catch {
        // ignore malformed attribution cache
      }
    }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInterestTag = (tag) => {
    setInterestTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const validateReferralCode = async (code) => {
    const clean = code.trim();
    if (!clean) {
      setReferralStatus("idle");
      return;
    }
    setReferralStatus("checking");
    try {
      const res = await api.get("/referral/validate", { params: { code: clean } });
      if (res.data.valid) {
        setReferralStatus("valid");
        setReferralExecName(res.data.company_name || res.data.marketing_executive_name || "");
      } else {
        setReferralStatus("invalid");
        setReferralExecName("");
      }
    } catch {
      setReferralStatus("invalid");
    }
  };

  const handleReferralChange = (v) => {
    setReferralCode(v);
    setReferralLocked(false);
    setReferralStatus("idle");
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    if (referralStatus !== "valid") {
      toast.error("Please enter a valid Counsellor / Marketing Executive Referral Code before continuing");
      return;
    }
    setLoading(true);
    try {
      const referral = attribution && attribution.referral_code === referralCode.trim().toUpperCase()
        ? attribution
        : { referral_code: referralCode.trim().toUpperCase() };
      await api.post("/student/profile/complete", {
        ...form,
        interest_tags: interestTags,
        interest_notes: interestNotes,
        referral,
      });
      toast.success("Profile saved");
      const otpRes = await api.post("/student/phone/send-otp");
      setDevOtp(otpRes.data.dev_otp);
      toast.info(`Demo Mode: your verification code is ${otpRes.data.dev_otp} (no SMS gateway connected yet)`);
      setStep("phone");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    const res = await api.post("/student/phone/send-otp");
    setDevOtp(res.data.dev_otp);
    toast.info(`Demo Mode: your verification code is ${res.data.dev_otp}`);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/student/phone/verify-otp", { code: otpInput });
      toast.success("Mobile number verified");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12" data-testid="onboarding-page">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-sm p-10">
        <div className="flex items-center justify-between gap-2 mb-8">
          <BrandLogo className="h-9" />
          <Button
            variant="ghost"
            size="icon"
            data-testid="onboarding-logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>

        {step === "profile" && (
          <form onSubmit={submitProfile} className="space-y-4" data-testid="onboarding-profile-form">
            <h1 className="font-heading text-xl font-bold text-slate-900 mb-1">Complete Your Profile</h1>
            <p className="text-sm text-slate-500 mb-6">Verified email and mobile are required before activation.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input data-testid="onboarding-first-name-input" required value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input data-testid="onboarding-last-name-input" required value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input data-testid="onboarding-dob-input" type="date" required value={form.dob} onChange={(e) => update("dob", e.target.value)} />
            </div>
            <div>
              <Label>Mobile Number</Label>
              <Input data-testid="onboarding-phone-input" required placeholder="+91 98765 43210" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Country</Label>
                <Input data-testid="onboarding-country-input" required value={form.country} onChange={(e) => update("country", e.target.value)} />
              </div>
              <div>
                <Label>State</Label>
                <Input data-testid="onboarding-state-input" required value={form.state} onChange={(e) => update("state", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Class / Education Level</Label>
              <Select value={form.class_level} onValueChange={(v) => update("class_level", v)}>
                <SelectTrigger data-testid="onboarding-class-select"><SelectValue placeholder="Select class/level" /></SelectTrigger>
                <SelectContent>
                  {CLASS_LEVELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>School / College</Label>
              <Input data-testid="onboarding-school-input" required value={form.school} onChange={(e) => update("school", e.target.value)} />
            </div>
            <div className="border-t border-slate-100 pt-4">
              <Label>Your Interests & Hobbies (optional)</Label>
              <p className="text-xs text-slate-400 mb-3">
                Helps us add a bit more personal context to your report — this never replaces the assessment itself.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {INTEREST_TAGS.map((tag) => (
                  <label key={tag} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid={`interest-tag-label-${tag.slice(0, 8)}`}>
                    <Checkbox
                      data-testid={`interest-tag-checkbox-${tag.slice(0, 8)}`}
                      checked={interestTags.includes(tag)}
                      onCheckedChange={() => toggleInterestTag(tag)}
                    />
                    {tag}
                  </label>
                ))}
              </div>
              <Textarea
                data-testid="onboarding-interest-notes-input"
                placeholder="Anything else about your hobbies or favourite pastimes? (optional)"
                rows={2}
                value={interestNotes}
                onChange={(e) => setInterestNotes(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label>Counsellor / Marketing Executive Referral Code</Label>
              <div className="relative">
                <Input
                  data-testid="onboarding-referral-code-input"
                  required
                  placeholder="e.g. K3F9XQ2"
                  value={referralCode}
                  disabled={referralLocked}
                  onChange={(e) => handleReferralChange(e.target.value.toUpperCase())}
                  onBlur={() => !referralLocked && validateReferralCode(referralCode)}
                  className="font-mono uppercase pr-9"
                />
                {referralStatus === "checking" && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
                {referralStatus === "valid" && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />}
                {referralStatus === "invalid" && <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rose-500" />}
              </div>
              {referralStatus === "valid" && (
                <p className="text-xs text-emerald-600 mt-1.5" data-testid="referral-code-verified-message">
                  ✓ Verified{referralExecName ? ` — ${referralExecName}` : ""}
                </p>
              )}
              {referralStatus === "invalid" && (
                <p className="text-xs text-rose-500 mt-1.5" data-testid="referral-code-invalid-message">
                  Invalid code. Please check with your marketing executive or counsellor and re-enter it.
                </p>
              )}
              {referralStatus === "idle" && (
                <p className="text-xs text-slate-400 mt-1.5">
                  Auto-filled if you used your counsellor's link/QR code. Otherwise, ask your marketing executive for this code — it's required to continue.
                </p>
              )}
            </div>
            <Button data-testid="onboarding-profile-submit-button" disabled={loading || referralStatus !== "valid"} type="submit" className="w-full h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 mt-2">
              Continue
            </Button>
          </form>
        )}

        {step === "phone" && (
          <form onSubmit={verifyOtp} className="space-y-5" data-testid="onboarding-otp-form">
            <ShieldCheck className="h-9 w-9 text-indigo-600" />
            <h1 className="font-heading text-xl font-bold text-slate-900">Verify Your Mobile Number</h1>
            <p className="text-sm text-slate-500">
              Enter the 6-digit code shown below. <span className="font-medium text-amber-600">Demo Mode: no SMS gateway connected yet.</span>
            </p>
            {devOtp && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 font-mono text-lg text-amber-700 text-center" data-testid="dev-otp-display">
                {devOtp}
              </div>
            )}
            <Input data-testid="onboarding-otp-input" required placeholder="Enter code" value={otpInput} onChange={(e) => setOtpInput(e.target.value)} />
            <Button data-testid="onboarding-otp-submit-button" disabled={loading} type="submit" className="w-full h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700">
              Verify & Continue
            </Button>
            <button type="button" data-testid="onboarding-resend-otp-button" onClick={resendOtp} className="text-xs text-indigo-600 hover:underline w-full text-center">
              Resend code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
